import React from 'react';
import { PageSection, TeamSectionContent, TeamMember } from '../../types/page';
import { Users, Linkedin, Twitter, Mail } from 'lucide-react';

interface TeamSectionProps {
  section: PageSection;
  className?: string;
}

export const TeamSection: React.FC<TeamSectionProps> = ({
  section,
  className = ''
}) => {
  const content = section.content as TeamSectionContent;

  // Default team members if none provided
  const defaultTeamMembers: TeamMember[] = [
    {
      name: "Carlos Rodríguez",
      role: "Director General",
      bio: "Más de 15 años de experiencia en el sector inmobiliario, liderando proyectos de gran envergadura.",
      image: "/images/team/member-1.jpg",
      social: {
        linkedin: "#",
        email: "carlos@empresa.com"
      }
    },
    {
      name: "María González",
      role: "Gerente de Ventas",
      bio: "Especialista en estrategias comerciales y desarrollo de negocios inmobiliarios.",
      image: "/images/team/member-2.jpg",
      social: {
        linkedin: "#",
        email: "maria@empresa.com"
      }
    },
    {
      name: "Luis Torres",
      role: "Asesor Inmobiliario Senior",
      bio: "Experto en propiedades residenciales y comerciales con enfoque en satisfacción del cliente.",
      image: "/images/team/member-3.jpg",
      social: {
        linkedin: "#",
        twitter: "#",
        email: "luis@empresa.com"
      }
    },
    {
      name: "Ana Martínez",
      role: "Especialista en Marketing",
      bio: "Creativa estratega digital con pasión por posicionar propiedades en el mercado.",
      image: "/images/team/member-4.jpg",
      social: {
        linkedin: "#",
        email: "ana@empresa.com"
      }
    }
  ];

  const teamMembers = content.items && content.items.length > 0 ? content.items : defaultTeamMembers;

  const backgroundStyle = content.backgroundImage
    ? { backgroundImage: `url(${content.backgroundImage})` }
    : {};

  return (
    <section
      className={`relative py-20 bg-cover bg-center bg-no-repeat ${className}`}
      style={backgroundStyle}
      aria-labelledby="team-section-title"
    >
      {/* Overlay */}
      {content.backgroundImage && (
        <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
      )}

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6">
            <Users className="w-8 h-8" aria-hidden="true" />
          </div>

          <h2
            id="team-section-title"
            className={`text-3xl md:text-4xl font-bold mb-4 ${
              content.backgroundImage ? 'text-white' : 'text-gray-900'
            }`}
          >
            {section.title || content.title || 'Nuestro Equipo'}
          </h2>

          {section.subtitle && (
            <p className={`text-xl max-w-2xl mx-auto ${
              content.backgroundImage ? 'text-gray-200' : 'text-gray-600'
            }`}>
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {teamMembers.map((member, index) => (
            <article
              key={index}
              className={`group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-105 ${
                content.backgroundImage
                  ? 'bg-white/10 backdrop-blur-sm border border-white/20'
                  : 'bg-white shadow-lg border border-gray-100 hover:shadow-2xl'
              }`}
            >
              {/* Member Image */}
              <div className="relative h-64 overflow-hidden">
                {member.image ? (
                  <img
                    src={member.image}
                    alt={`Foto de ${member.name}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                ) : (
                  <div className={`w-full h-full flex items-center justify-center ${
                    content.backgroundImage ? 'bg-white/5' : 'bg-gray-200'
                  }`}>
                    <Users className={`w-16 h-16 ${
                      content.backgroundImage ? 'text-white/50' : 'text-gray-400'
                    }`} aria-hidden="true" />
                  </div>
                )}

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Member Info */}
              <div className="p-6">
                <h3 className={`text-xl font-bold mb-1 ${
                  content.backgroundImage ? 'text-white' : 'text-gray-900'
                }`}>
                  {member.name}
                </h3>

                <p className={`text-sm font-medium mb-3 ${
                  content.backgroundImage ? 'text-blue-300' : 'text-blue-600'
                }`}>
                  {member.role}
                </p>

                {member.bio && (
                  <p className={`text-sm leading-relaxed mb-4 ${
                    content.backgroundImage ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {member.bio}
                  </p>
                )}

                {/* Social Links */}
                {member.social && (
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200/20">
                    {member.social.linkedin && (
                      <a
                        href={member.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`transition-colors ${
                          content.backgroundImage
                            ? 'text-gray-300 hover:text-white'
                            : 'text-gray-500 hover:text-blue-600'
                        }`}
                        aria-label={`Perfil de LinkedIn de ${member.name}`}
                      >
                        <Linkedin className="w-5 h-5" />
                      </a>
                    )}

                    {member.social.twitter && (
                      <a
                        href={member.social.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`transition-colors ${
                          content.backgroundImage
                            ? 'text-gray-300 hover:text-white'
                            : 'text-gray-500 hover:text-blue-600'
                        }`}
                        aria-label={`Perfil de Twitter de ${member.name}`}
                      >
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}

                    {member.social.email && (
                      <a
                        href={`mailto:${member.social.email}`}
                        className={`transition-colors ${
                          content.backgroundImage
                            ? 'text-gray-300 hover:text-white'
                            : 'text-gray-500 hover:text-blue-600'
                        }`}
                        aria-label={`Enviar correo a ${member.name}`}
                      >
                        <Mail className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center">
          <p className={`text-sm ${
            content.backgroundImage ? 'text-gray-300' : 'text-gray-600'
          }`}>
            ¿Desea unirse a nuestro equipo?{' '}
            <a
              href="/contacto"
              className={`font-semibold underline hover:no-underline ${
                content.backgroundImage ? 'text-white' : 'text-blue-600 hover:text-blue-700'
              }`}
              aria-label="Ir a la página de contacto para oportunidades laborales"
            >
              Contáctenos
            </a>
            {' '}para conocer nuestras oportunidades.
          </p>
        </div>
      </div>
    </section>
  );
};
